import {useAuth} from '@apps-common/auth-react';
import {PolygonPlus} from '@apps-common/ui-icons';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Fab,
  IconButton,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import {useDebounce} from '@uidotdev/usehooks';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import CollectionsList from './CollectionsList.jsx';
import FilterMenu from './FilterMenu.jsx';
import {dismissNotification, notify} from '@actions/notifications';
import {showModal} from '@app/actions/ui';
import {trackEvent} from '@app/analytics';
import {useGetInfiniteCollections} from '@app/api/collections';
import EmptyMessage from '@app/components/common/EmptyMessage';
import ErrorMessage from '@app/components/common/ErrorMessage';
import FilterMenuButton from '@app/components/common/FilterMenuButton';
import FilterTextField from '@app/components/common/FilterTextField';
import LoadingMessage from '@app/components/common/LoadingMessage';

import {MODALS, TOUR_STAGES} from '@app/constants';
import useOrgParams from '@app/hooks/useOrgParams';
import {isUsersOrg} from '@app/util/user';
import DrawerHeader from '@components/common/DrawerHeader';

export const COLLECTION_OFFSET_SIZE = 20;
export const NO_ORG_ACCESS_ERR_KEY = 'no-org-access';

const StyledFilterTextField = styled(FilterTextField)({
  animationName: 'openSearch',
  animationDuration: '.3s',
  animationDirection: 'normal',
  '@keyframes openSearch': {
    '0%': {width: '0%'},
    '100%': {width: '100%'},
  },
});

const CollectionsPanel = () => {
  const dispatch = useDispatch();
  const {user} = useAuth();
  const {orgId, setOrg} = useOrgParams();
  const isUserOrg = isUsersOrg(user?.claims, orgId);

  const [collectionSearchValue, setCollectionSearchValue] = useState('');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);

  const debouncedSearchFilter = useDebounce(collectionSearchValue, 250);

  const totalFiltersApplied = useMemo(
    () => Object.values(filters).filter(perm => perm).length,
    [filters]
  );

  const hasFiltersApplied = useMemo(
    () => !!debouncedSearchFilter || totalFiltersApplied > 0,
    [debouncedSearchFilter, totalFiltersApplied]
  );

  const resetAllFilters = () => {
    setFilters({});
  };

  useEffect(() => {
    resetAllFilters();
  }, [orgId]);

  const toggleFilterMenu = () => {
    setFilterMenuOpen(!filterMenuOpen);
    trackEvent('Filter Menu', {
      action: filterMenuOpen ? 'closed' : 'opened',
    });
  };

  const {
    data: collectionsData,
    isPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useGetInfiniteCollections({
    orgId,
    title: debouncedSearchFilter ? `~${debouncedSearchFilter}` : undefined,
    ...filters,
  });

  const {collections, numberMatched: totalCollections} = collectionsData || {};

  useEffect(() => {
    if (
      error?.response?.status === 403 &&
      orgId &&
      orgId !== user?.claims?.organization_id
    ) {
      dispatch(
        notify({
          message: `You do not have access to view the collections for organization ${orgId}. Please switch back to your own organization (403)`,
          severity: 'error',
          persist: true,
          key: NO_ORG_ACCESS_ERR_KEY,
        })
      );
    }
  }, [dispatch, orgId, user?.claims?.organization_id, error?.response?.status]);

  const renderContent = () => {
    if (isPending && !isFetchingNextPage) {
      return <LoadingMessage suffix="collections" />;
    }
    if (error) {
      if (
        error?.response?.status === 403 &&
        orgId &&
        orgId !== user?.claims?.organization_id
      ) {
        return (
          <ErrorMessage
            message="You do not have access to view collections for the selected organization"
            statusCode={error?.response?.status}
            actions={[
              <Button
                key="resetOrg"
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => {
                  setOrg(user?.claims?.organization_id);
                  dispatch(dismissNotification(NO_ORG_ACCESS_ERR_KEY));
                }}
              >
                Switch back to your organization
              </Button>,
            ]}
          />
        );
      }
      return <ErrorMessage message={error?.message} />;
    }
    if (!collections?.length && !hasFiltersApplied) {
      return (
        <EmptyMessage
          icon={<PolygonPlus />}
          title="No existing collections"
          description={
            !isUsersOrg(user?.claims, orgId)
              ? 'This organization does not have any collections'
              : ''
          }
          actions={
            isUsersOrg(user?.claims, orgId)
              ? [
                  <Button
                    key="upload"
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      trackEvent('Open upload modal from left drawer');
                      dispatch(showModal(MODALS.UPLOAD));
                    }}
                    sx={{marginTop: 1}}
                    data-tour={TOUR_STAGES.UPLOAD_AOI}
                  >
                    Upload AOIs
                  </Button>,
                ]
              : []
          }
        />
      );
    }
    if (collections.length === 0 && hasFiltersApplied) {
      return (
        <EmptyMessage
          title="Sorry, no collections were found."
          description="Try refining your search"
        />
      );
    }
    return (
      <>
        <CollectionsList collections={collections} />
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            alignSelf: 'center',
          }}
        >
          {hasNextPage && (
            <Fab
              disabled={isFetchingNextPage}
              onClick={() => {
                fetchNextPage();
              }}
              size="small"
              sx={{
                backgroundColor: 'grey.100',
                '&:hover': {
                  backgroundColor: 'grey.200',
                },
              }}
              variant="extended"
            >
              {isFetchingNextPage ? 'Loading...' : 'Load More'}
              <ArrowDropDownIcon fontSize="small" />
            </Fab>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontStyle: 'italic',
            paddingX: 2,
            paddingY: 1,
            color: 'text.secondary',
          }}
        >
          Viewing {collections.length}/{totalCollections} collections
        </Typography>
      </>
    );
  };

  return (
    <>
      {filterMenuOpen ? (
        <FilterMenu
          onClose={toggleFilterMenu}
          filters={filters}
          setFilters={setFilters}
          resetFilters={resetAllFilters}
        />
      ) : (
        <>
          <DrawerHeader
            data-tour={TOUR_STAGES.COLLECTIONS}
            avatar={
              searchOpen ? undefined : (
                <IconButton
                  onClick={() => setSearchOpen(!searchOpen)}
                  aria-label="open search"
                  size="small"
                  color="default"
                >
                  <SearchIcon />
                </IconButton>
              )
            }
            title={
              searchOpen ? (
                <StyledFilterTextField
                  size="small"
                  fullWidth
                  margin="none"
                  type="search"
                  placeholder="Search by collection name..."
                  value={collectionSearchValue}
                  onChange={value => setCollectionSearchValue(value)}
                  disabled={!collections?.length && !hasFiltersApplied}
                  component={Paper}
                  elevation={0}
                />
              ) : (
                'Collections'
              )
            }
            disableTypography={searchOpen}
            action={
              searchOpen ? (
                <IconButton
                  onClick={() => setSearchOpen(!searchOpen)}
                  aria-label="close search"
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              ) : (
                <>
                  <FilterMenuButton
                    badgeCount={totalFiltersApplied}
                    tooltipText="Filter"
                    tooltipPlacement="left"
                    onClick={toggleFilterMenu}
                    disabled={!collectionsData}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!isUserOrg}
                    onClick={() => dispatch(showModal(MODALS.UPLOAD))}
                    startIcon={<ArrowCircleUpIcon fontSize="small" />}
                    sx={{marginLeft: 1.25}}
                    data-testid="header-upload-button"
                  >
                    Upload
                  </Button>
                </>
              )
            }
          />
          {renderContent()}
        </>
      )}
    </>
  );
};

export default CollectionsPanel;
